import { styled } from "@linaria/react";
import React from "react";
import { color, fonts } from "..";
import Button from "../Button";
import Checkbox from "../Checkbox";

export const SELECTABLE_ROW_CHECKBOX_CLASSNAME = "selectable-row-checkbox";
export const SELECTED_CLASSNAME = "selected";
export const SELECTABLE_ROWS_CLASSNAME = "selectable-rows";

interface P<Data> {
  computeRowKey: (d: Data) => string;
  tableRef: React.RefObject<HTMLTableElement>;
  datas: Data[];
  selectedDatas?: Data[];
  onRowsSelection?: (datas: Data[]) => void;
}

export const ROW_SELECTOR_PADDING_PX = 16;
export const ROW_SELECTOR_SIZE_PX = 16;

export const RowSelector = styled(Checkbox)`
  font: ${fonts.body2};
  width: ${ROW_SELECTOR_SIZE_PX}px;
  height: ${ROW_SELECTOR_SIZE_PX}px;
  position: absolute;
  left: ${ROW_SELECTOR_PADDING_PX}px;
`;

function useSelectedRows<Data>({
  computeRowKey,
  datas,
  onRowsSelection,
  tableRef,
  selectedDatas,
}: P<Data>) {
  const [selectAllStatus, setSelectAllStatus] = React.useState<"empty" | "partial" | "full">(
    "empty",
  );
  const rowSelectionDefaultRef = React.useRef(selectAllStatus === "full");
  rowSelectionDefaultRef.current = selectAllStatus === "full";

  // store all rowKeys of selected rows, even hidden ones (filtering)
  // what will not be sent to onRowsSelection
  const selectedRowKeysRef = React.useRef<string[]>(
    selectedDatas ? selectedDatas.map(data => computeRowKey(data)) : [],
  );
  selectedRowKeysRef.current = selectedDatas ? selectedDatas.map(data => computeRowKey(data)) : [];

  const isRowSelected = React.useCallback(
    (data: Data) =>
      selectedDatas &&
      selectedDatas.length >= 1 &&
      selectedRowKeysRef.current.includes(computeRowKey(data))
        ? true
        : false,
    [computeRowKey, selectedDatas],
  );

  const decorateTrs = React.useCallback(() => {
    const table = tableRef.current;
    if (!table) throw new Error("Missing table");

    window.requestAnimationFrame(() => {
      const inputs = Array.from(
        table.querySelectorAll<HTMLInputElement>(`.${SELECTABLE_ROW_CHECKBOX_CLASSNAME} input`),
      );

      for (const input of inputs) {
        const trElement: HTMLTableRowElement | null = input.closest("tr");
        if (!trElement) throw new Error("Cannot find tr element");
        if (input.checked) {
          trElement.classList.add(SELECTED_CLASSNAME);
        } else {
          trElement.classList.remove(SELECTED_CLASSNAME);
        }
      }
    });
  }, [tableRef]);

  const readRowsSelectionFromDom = React.useCallback(() => {
    const table = tableRef.current;
    if (!table) throw new Error("Missing table");
    // In some cases, selected row are not cleaned so we force it.
    if (!onRowsSelection) {
      for (const e of table.querySelectorAll<HTMLTableRowElement>("tr.selected"))
        e.classList.remove(SELECTED_CLASSNAME);
      return;
    }
    const selectedRows = Array.from(
      table.querySelectorAll<HTMLInputElement>(
        `.${SELECTABLE_ROW_CHECKBOX_CLASSNAME} input:checked`,
      ),
    ).map(e => e.value);
    const unselectedRows = Array.from(
      table.querySelectorAll<HTMLInputElement>(
        `.${SELECTABLE_ROW_CHECKBOX_CLASSNAME} input:not(:checked)`,
      ),
    ).map(e => e.value);
    const allSelectableRows = [...selectedRows, ...unselectedRows];

    // special sauce: remove unselected keys and add new selected keys
    // without touching invisible selected keys (filtered out by active search for example),
    // so that they will magically come back when filtering is removed
    selectedRowKeysRef.current = Array.from(
      new Set([...selectedRowKeysRef.current, ...selectedRows]),
    ).filter(k => !unselectedRows.includes(k));

    decorateTrs();

    // but remove invisible selected rows from final payload
    onRowsSelection(datas.filter(d => selectedRowKeysRef.current.includes(computeRowKey(d))));
    setSelectAllStatus(
      allSelectableRows.length === 0 || selectedRows.length === 0
        ? "empty"
        : selectedRows.length === allSelectableRows.length
          ? "full"
          : "partial",
    );
  }, [computeRowKey, datas, onRowsSelection, tableRef, decorateTrs]);

  const selectAllRows = React.useCallback(
    (selectAll: boolean) => {
      if (!onRowsSelection) return;
      const table = tableRef.current;
      if (!table) throw new Error("Missing table");
      const inputs = Array.from(
        table.querySelectorAll<HTMLInputElement>(`.${SELECTABLE_ROW_CHECKBOX_CLASSNAME} input`),
      );
      window.requestAnimationFrame(() => {
        for (const input of inputs) {
          input.checked = selectAll;
        }
      });
      for (const input of inputs) {
        if (selectAll) {
          // add it if absent
          if (!selectedRowKeysRef.current.includes(input.value))
            selectedRowKeysRef.current = [...selectedRowKeysRef.current, input.value];
        } else {
          // remove it if present
          selectedRowKeysRef.current = selectedRowKeysRef.current.filter(
            key => key !== input.value,
          );
        }
      }

      decorateTrs();
      setSelectAllStatus(selectAll ? "full" : "empty");
      onRowsSelection(datas.filter(d => selectedRowKeysRef.current.includes(computeRowKey(d))));
    },
    [computeRowKey, datas, onRowsSelection, tableRef, decorateTrs],
  );

  React.useLayoutEffect(readRowsSelectionFromDom, [readRowsSelectionFromDom]);

  return {
    isRowSelected,
    selectAllRows,
    readRowsSelectionFromDom,
    selectAllStatus,
  };
}

export const CancelSelection = styled(Button)`
  font: ${fonts.body2};
  color: ${color.lightNitrogen500_Main};
  text-decoration: underline;
  padding: 0;
`;

export const AddSelection = styled(Button)`
  font: ${fonts.body1};
  :hover {
    background-color: ${color.titanium100};
  }
`;

export default useSelectedRows;
